import React from 'react';
import { ArrowLeft, Tag, ChevronRight } from 'lucide-react';
import { Service } from './types';
import { getThemeTokens } from '@/lib/themeTokens';

interface ClientWhatsAppViewProps {
  businessName?: string;
  portalTheme?: string;
  brandColor?: string;
  primaryService: Service;
  selectedServices: Service[];
  assignedStaffName: string;
  formattedDateString: string;
  selectedTime: string;
  phone: string;
  onChangePhone: (val: string) => void;
  onProceed: () => void;
  onBack: () => void;
}

export const ClientWhatsAppView: React.FC<ClientWhatsAppViewProps> = ({
  businessName = 'Reservá tu turno',
  portalTheme,
  brandColor,
  primaryService,
  selectedServices,
  assignedStaffName,
  formattedDateString,
  selectedTime,
  phone,
  onChangePhone,
  onProceed,
  onBack,
}) => {
  const theme = getThemeTokens(portalTheme, brandColor);
  const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);

  // Limpiar sólo números
  const cleanDigits = phone.replace(/[^0-9]/g, '');
  const isValidPhone = cleanDigits.length >= 8;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValidPhone) {
      onProceed();
    }
  };

  return (
    <div style={{ color: theme.textPrimary }} className="w-full max-w-5xl mx-auto px-4 pb-24">
      {/* Barra superior con nombre de negocio y botón volver */}
      <div className="flex items-center gap-3 mb-6 pt-2">
        <button
          onClick={onBack}
          aria-label="Volver a horarios"
          style={{
            backgroundColor: theme.bgSurface,
            borderColor: theme.borderSubtle,
            color: theme.textPrimary
          }}
          className="w-10 h-10 rounded-full border flex items-center justify-center hover:opacity-85 transition shadow-xs cursor-pointer shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1
          style={{ color: theme.textPrimary }}
          className="text-xl sm:text-2xl font-bold tracking-tight"
        >
          {businessName}
        </h1>
      </div>

      {/* Grid en 2 Columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: Formulario WhatsApp-First */}
        <div className="lg:col-span-7">
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-8 sm:p-10 shadow-xs border flex flex-col items-center text-center"
          >
            <h2
              style={{ color: theme.textPrimary }}
              className="text-2xl sm:text-3xl font-bold tracking-tight"
            >
              ¿Ya sos cliente?
            </h2>
            <p
              style={{ color: theme.textSecondary }}
              className="text-xs sm:text-sm font-medium max-w-xs mt-2 leading-relaxed"
            >
              Ingresá tu WhatsApp. Si ya te registraste, completamos tus datos automáticamente.
            </p>

            {/* Input telefónico con bandera argentina y prefijo */}
            <div className="w-full max-w-md mt-7">
              <div
                style={{
                  backgroundColor: theme.bgPage,
                  borderColor: theme.borderSubtle
                }}
                className="border-2 rounded-2xl flex items-center px-4 py-3 shadow-2xs focus-within:ring-2 focus-within:ring-slate-400 transition"
              >
                {/* Bandera y código de país */}
                <div
                  style={{ borderColor: theme.borderSubtle }}
                  className="flex items-center gap-2 border-r pr-3 mr-3 shrink-0 select-none"
                >
                  <span className="text-xl">🇦🇷</span>
                  <span
                    style={{ color: theme.textPrimary }}
                    className="font-bold text-sm sm:text-base"
                  >
                    +54 9
                  </span>
                </div>

                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => onChangePhone(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="11 2345 6789"
                  autoFocus
                  style={{ color: theme.textPrimary }}
                  className="w-full font-bold text-base placeholder-slate-400 bg-transparent focus:outline-hidden"
                />
              </div>

              {/* Botón Completá tu WhatsApp */}
              <button
                disabled={!isValidPhone}
                onClick={onProceed}
                style={{
                  backgroundColor: isValidPhone ? theme.brandButtonBg : theme.bgSubtle,
                  color: isValidPhone ? theme.brandButtonText : theme.textMuted,
                  borderColor: theme.borderSubtle
                }}
                className={`w-full py-4 rounded-2xl font-bold text-sm sm:text-base mt-4 border transition duration-200 shadow-xs ${
                  isValidPhone ? 'hover:opacity-90 cursor-pointer' : 'cursor-not-allowed opacity-60'
                }`}
              >
                {isValidPhone ? 'Continuar →' : 'Completá tu WhatsApp'}
              </button>
            </div>
          </div>
        </div>

        {/* Columna Derecha: Tarjeta Resumen */}
        <div className="lg:col-span-5">
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-6 shadow-xs border flex flex-col gap-4"
          >
            {/* Lista de servicios */}
            <div className="flex flex-col gap-3">
              {selectedServices.map((srv) => (
                <div key={srv.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      style={{ backgroundColor: theme.bgSubtle }}
                      className="w-12 h-12 rounded-xl overflow-hidden shrink-0"
                    >
                      <img
                        src={srv.imageUrl || 'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=200&h=200&fit=crop'}
                        alt={srv.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3
                        style={{ color: theme.textPrimary }}
                        className="text-sm font-bold truncate"
                      >
                        {srv.name}
                      </h3>
                      <p style={{ color: theme.textSecondary }} className="text-xs">
                        👤 con {assignedStaffName}
                      </p>
                    </div>
                  </div>

                  <span
                    style={{ color: theme.textPrimary }}
                    className="font-bold text-sm whitespace-nowrap"
                  >
                    ${' '}
                    {srv.cashPrice.toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </div>
              ))}
            </div>

            {/* Fecha y hora */}
            <div
              style={{
                backgroundColor: theme.bgSubtle,
                borderColor: theme.borderSubtle
              }}
              className="border rounded-xl p-3 flex items-center justify-between text-xs font-semibold"
            >
              <span style={{ color: theme.textMuted }}>Fecha y hora</span>
              <span style={{ color: theme.textPrimary }} className="font-bold">
                {formattedDateString} · {selectedTime} Hs
              </span>
            </div>

            <hr style={{ borderColor: theme.borderSubtle }} />

            {/* Fila Total */}
            <div className="flex items-center justify-between">
              <span style={{ color: theme.textPrimary }} className="text-sm font-bold">💵 Total</span>
              <span style={{ color: theme.textPrimary }} className="text-xl sm:text-2xl font-black">
                ${' '}
                {totalCashPrice.toLocaleString('es-AR', {
                  minimumFractionDigits: 0,
                })}
              </span>
            </div>

            <button
              style={{ color: theme.textSecondary }}
              className="text-xs hover:opacity-80 flex items-center gap-1.5 self-start cursor-pointer"
            >
              <Tag className="w-3.5 h-3.5 opacity-60" />
              <span>¿Tenés un cupón?</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
